import { BalanceSheetAllow, IncomeStatementAllow } from 'api';
import {
  FIRST_ROW_ALIAS,
  FISCAL_YEAR,
  FISCAL_YEAR_PREFIX,
  FORMAT_YEAR_TITLE,
  LTM,
  NTM,
  SECOND_ROW_ALIAS,
} from 'common/constants/financials';
import { FinancialsPerformanceMetricsColumn, FinancialStatementPeriod, PeriodType } from 'common/types/financials';

export const getPeriodTypeFromIncomeStatement = (incomeStatementColumn: FinancialStatementPeriod) => {
  const { period_type, isLTMYear, isNTMYear } = incomeStatementColumn;
  if (period_type) {
    return period_type;
  }

  if (isLTMYear) {
    return LTM;
  }
  if (isNTMYear) {
    return NTM;
  }
  return FISCAL_YEAR;
};

export const getSuffixFromIncomeStatement = (
  incomeStatementColumn: FinancialStatementPeriod,
  periodType: PeriodType
) => {
  const { subtitle, isProjectedYear, name } = incomeStatementColumn;

  // Is a historical year
  if (periodType === FISCAL_YEAR && !isProjectedYear) {
    // If name already has FISCAL_YEAR_PREFIX, return name
    if (name?.includes(FISCAL_YEAR_PREFIX)) {
      return `-${name}`;
    }
    return `-${FORMAT_YEAR_TITLE(name)}`;
  }
  // Is a projection year
  // If name already has FISCAL_YEAR_PREFIX, return name
  if (subtitle?.includes(FISCAL_YEAR_PREFIX)) {
    return `-${subtitle}`;
  }
  return `-${FORMAT_YEAR_TITLE(subtitle!)}`;
};

export const getMapKeyFromIncomeStatement = (incomeStatementColumn: FinancialStatementPeriod) => {
  const periodType = getPeriodTypeFromIncomeStatement(incomeStatementColumn);

  if ([LTM, NTM].includes(periodType)) {
    return periodType;
  }

  const suffix = getSuffixFromIncomeStatement(incomeStatementColumn, periodType as PeriodType);

  return `${periodType}${suffix}`;
};

export const generateKeyForMap = (periodType: PeriodType, statementDate: string) => {
  if (periodType === FISCAL_YEAR) {
    const [year] = statementDate.split('-');
    return `${periodType}-${FORMAT_YEAR_TITLE(year)}`;
  }
  if ([LTM, NTM].includes(periodType)) {
    return periodType;
  }
  return `${periodType}-${statementDate}`;
};

/**
 *
 * @param {Array} validPeriods
 * @param {boolean} addHistoricalYear
 * @returns {number} index of the reference period
 */
export const getReferenceIndex = (validPeriods: FinancialsPerformanceMetricsColumn[], addHistoricalYear: boolean) => {
  if (addHistoricalYear) {
    return 0;
  }

  for (let index = validPeriods.length - 1; index >= 0; index -= 1) {
    if (
      validPeriods[index]?.is_projection &&
      validPeriods[index]?.isLTM !== true &&
      validPeriods[index]?.isNTM !== true &&
      validPeriods[index]?.is_deleted !== true
    ) {
      return index;
    }
  }
  return undefined;
};

export const getColumnValue = ({
  rowAlias,
  isColumnWithReportedDate,
  column,
  columnTableData,
}: {
  rowAlias: string;
  isColumnWithReportedDate: boolean;
  column: FinancialStatementPeriod;
  columnTableData?: IncomeStatementAllow | BalanceSheetAllow;
}) => {
  if (rowAlias === SECOND_ROW_ALIAS && isColumnWithReportedDate) {
    return columnTableData?.reported_statement_date ?? column.statement_date;
  }
  
  // For title and subtitle rows, always use the column values (not database values)
  // because the database only has 'name' field, not 'title'/'subtitle'
  if (rowAlias === FIRST_ROW_ALIAS || rowAlias === SECOND_ROW_ALIAS) {
    return column[rowAlias as keyof FinancialStatementPeriod];
  }
  
  return (
    columnTableData?.[rowAlias as keyof (IncomeStatementAllow | BalanceSheetAllow)] ??
    column[rowAlias as keyof FinancialStatementPeriod]
  );
};
